import { useEffect, memo, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  fetchNotifications,
  dismissCriticalNotification,
} from 'services/misc/miscAsync';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getCriticalNotifications,
  getCriticalNotificationsLoadingStatus,
  setCriticalNotificationsData,
} from 'services/misc/criticalNotificationSlice';
import Alert from 'components/Alert';
import LazySvg from 'components/LazySvg';
import If from 'components/If';
import { logErrors } from 'utils';
import {
  selectIsEligibleForDataCollection,
  selectIsDataCollectionBannerClosed,
} from 'services/misc/miscSelectors';
import DataCollectionBanner from 'pages/DataCollectionModal/banner';
import announceLayoutShift from 'utils/layoutShift';
import styles from './index.module.scss';

// type
export type CriticalNotificationType = {
  id: string;
  message: string;
  notification: {
    /* BE flag — when true the notification is persisted on the
     * server and the client must call `PUT /notifications/{id}` to
     * dismiss it; when false the notification is transient and only
     * the local list needs updating. */
    userDeactivatable?: boolean;
    type?: string;
    severity?: string;
  };
};

function CriticalNotification() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getCriticalNotifications);
  const isNotificationsLoaded = useAppSelector(
    getCriticalNotificationsLoadingStatus,
  );
  const isEligibleForDataCollection = useAppSelector(
    selectIsEligibleForDataCollection,
  );
  const isDataCollectionBannerClosed = useAppSelector(
    selectIsDataCollectionBannerClosed,
  );

  // fetch critical notifications
  useEffect(() => {
    async function getNotifications() {
      try {
        await dispatch(fetchNotifications());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isNotificationsLoaded) {
      getNotifications();
    }
  }, []);

  /* This slot renders at the top of the content column, so whatever it shows
     moves every dashboard section below it. Both the PII banner and the
     notification list arrive from async fetches (and one can replace the other,
     changing the height again), all after the page has settled — so anything
     measuring the layout has to be told. See `utils/layoutShift`. Keyed on what
     is rendered rather than on the notification payloads, so it fires only when
     the height actually changes. */
  const visibleBanner = useMemo(() => {
    if (isEligibleForDataCollection && !isDataCollectionBannerClosed)
      return 'pii';
    /* The count is part of the key, not just the branch: the alerts stack, so
       dismissing one of several changes the height without changing which
       branch renders. */
    return notifications.length
      ? `notifications:${notifications.length}`
      : 'none';
  }, [
    isEligibleForDataCollection,
    isDataCollectionBannerClosed,
    notifications.length,
  ]);

  useEffect(() => announceLayoutShift(), [visibleBanner]);

  /* Angular parity — `critical-notifications.component.ts:183-190`.
   * Dismissing a notification takes one of two paths depending on
   * the BE's `userDeactivatable` flag:
   *   – userDeactivatable=true  → PUT /notifications/{id}, BE
   *     tombstones the row so it doesn't come back on the next
   *     fetch. We also remove it locally to give an instant response.
   *   – userDeactivatable=false → local removal only. These are
   *     things like the browser-unsupported banner that live only
   *     on the client and would come right back from the BE. */
  const handleNotificationClose = (item: CriticalNotificationType) => {
    if (item?.notification?.userDeactivatable && item?.id) {
      dispatch(dismissCriticalNotification(item.id)).catch(logErrors);
    }
    const updatedNotifications = notifications.filter((n) => n.id !== item.id);
    dispatch(setCriticalNotificationsData(updatedNotifications));
  };

  if (isEligibleForDataCollection && !isDataCollectionBannerClosed) {
    return (
      <div className={styles.bannerContainer}>
        <DataCollectionBanner />
      </div>
    );
  }

  return (
    <If condition={!!notifications.length}>
      <div className={styles.container}>
        {notifications.map((item: CriticalNotificationType) => (
          <Alert
            key={item?.id}
            type='error'
            message={
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item?.message ?? ''),
                }}
              />
            }
            closable={{
              'aria-label': 'close',
              closeIcon: (
                <LazySvg
                  name='close'
                  className={styles.closeIcon}
                  onClick={() => handleNotificationClose(item)}
                />
              ),
            }}
            /* antd owns the close: it collapses and unmounts the alert
               internally, so this component does not necessarily re-render and
               the effect above cannot see it. `afterClose` is antd's own
               post-collapse hook, which makes the announcement independent of
               whether the Redux list was updated. */
            afterClose={() => {
              announceLayoutShift();
            }}
            className={styles.notification}
          />
        ))}
      </div>
    </If>
  );
}

export default memo(CriticalNotification);
